import { useRef } from 'react'
import toast from 'react-hot-toast'
import { useFormik } from 'formik'

import UserOutlinedIcon from '@ant-design/icons/UserOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@components/_mui/Typography'

import AddressField from '@shared/components/src/AddressField'
import Image from '@shared/components/src/Image'
import Mask from '@shared/components/src/Mask'
import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { useMe } from '@shared/providers/src/MeProvider'
import { handleError, includesOneOfErrorMessages, states, UserRole } from '@shared/utils'

import AvatarUploader from '@components/AvatarUploader'
import Section from '@components/Details/Section'
import InputControl from '@components/InputControl'
import SignPad from '@components/SignPad'

import { useProfileUpdate } from '../MyProfile.hooks'
import { usePictureRemove, usePictureUpdate } from './Account.hooks'
import { getInitialValues, validationSchema, valuesToApiData } from './AccountTab.utils'

export default function AccountTab() {
  const signPadRef = useRef()

  const me = useMe()
  const isProvider = me.role === UserRole.Provider
  const isAdmin = me.role === UserRole.Admin
  const timezones = useLookup(Lookup.Timezones)
  const pronounsList = useLookup(Lookup.PronounsList)

  const updateProfile = useProfileUpdate()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(me),
    validationSchema,
    onSubmit: (values, { setFieldError }) => {
      let data = valuesToApiData(values, { isAdmin })

      if (isProvider) {
        const isEmpty = signPadRef.current?.isEmpty()

        if (!me.signature && isEmpty) {
          setFieldError('signature', 'Signature is required')
          return Promise.resolve()
        }

        const nameChanged = me.firstName !== values.firstName || me.lastName !== values.lastName
        if (nameChanged && isEmpty) {
          setFieldError('signature', 'Signature is required when First Name or Last Name changes')
          return Promise.resolve()
        }

        const signature = signPadRef.current.getTrimmedCanvas().toDataURL('image/png')
        if (!isEmpty) {
          data = {
            ...data,
            user: { ...data.user, signature },
          }
        }
      }

      return updateProfile
        .mutateAsync(data)
        .then(() => {
          toast.success('Profile updated')
          signPadRef.current?.clear()
        })
        .catch((e) => {
          if (includesOneOfErrorMessages(e, ['Phone invalid'])) {
            return setFieldError('phone', 'Invalid phone number')
          }
          handleError(e)
        })
    },
  })

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Section title="Basic details" icon={<UserOutlinedIcon />}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ProfilePicture />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="firstName" formikProps={formik}>
              <TextField
                fullWidth
                required
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              />
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="lastName" formikProps={formik}>
              <TextField
                fullWidth
                required
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              />
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="phone" formikProps={formik}>
              <TextField
                fullWidth
                required
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { inputComponent: Mask.Phone },
                }}
              />
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="email" formikProps={formik}>
              <TextField
                fullWidth
                required
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              />
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="pronouns" formikProps={formik}>
              {/* Users still should be able to update pronouns even if form is readonly */}
              <TextField autoFocus={!isAdmin} fullWidth select required disabled={formik.isSubmitting}>
                {pronounsList.map(({ key, label }) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="timezone" formikProps={formik}>
              <TextField fullWidth select required disabled={formik.isSubmitting || isProvider}>
                {timezones.map(({ key, label }) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </InputControl>
          </Grid>
        </Grid>
        <Typography variant="h4" component="h5" sx={{ p: 2, pt: 4 }}>
          Out of Office
        </Typography>
        <Box>
          <InputControl field="outOfOfficeMessage" formikProps={formik}>
            <TextField
              required
              multiline
              rows={3}
              label="Message"
              placeholder="Your out of office message goes here. It will be shown on your profile and to anyone trying to contact you."
              disabled={formik.isSubmitting}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </InputControl>
        </Box>
        <Typography variant="h4" component="h5" sx={{ p: 2, pt: 4 }}>
          Address
        </Typography>
        <Grid container spacing={3}>
          <Grid size={12}>
            <AddressField
              name="address"
              value={formik.values.address || ''}
              readOnly={!isAdmin}
              required
              disabled={formik.isSubmitting}
              label="Street Address"
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onPlaceSelect={({ address, city, state, zip }) => {
                formik.setFieldValue('address', address)
                if (city) formik.setFieldValue('city', city)
                if (state) formik.setFieldValue('state', state)
                if (zip) formik.setFieldValue('zip', zip)
              }}
            />
          </Grid>
          <Grid size={12}>
            <InputControl field="address2" formikProps={formik}>
              <TextField
                fullWidth
                label="Apartment, suite, floor"
                required
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              />
            </InputControl>
          </Grid>
          <Grid size={6}>
            <InputControl field="city" formikProps={formik}>
              <TextField
                label="City"
                required
                fullWidth
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              />
            </InputControl>
          </Grid>
          <Grid size={3}>
            <InputControl field="state" formikProps={formik}>
              <TextField
                label="State"
                required
                fullWidth
                select
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              >
                {states.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </InputControl>
          </Grid>
          <Grid size={3}>
            <InputControl field="zip" formikProps={formik}>
              <TextField
                label="Zip code"
                required
                fullWidth
                disabled={formik.isSubmitting}
                slotProps={{
                  input: { readOnly: !isAdmin },
                }}
              />
            </InputControl>
          </Grid>
        </Grid>
        {isProvider && (
          <>
            <Typography variant="h4" component="h5" sx={{ p: 2, pt: 4 }}>
              Signature
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3 }}>
              {me.signature && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={1}>
                    <InputLabel>Current Signature</InputLabel>
                    <Image
                      src={me.signature}
                      fit="contain"
                      duration={400}
                      sx={{
                        maxHeight: 110,
                        minWidth: 300,
                        borderRadius: 1,
                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : undefined),
                      }}
                    />
                  </Stack>
                </Grid>
              )}
              <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                <InputLabel>Update Signature</InputLabel>
                <SignPad ref={signPadRef} />
                {formik.errors.signature && <FormHelperText error>{formik.errors.signature}</FormHelperText>}
              </Stack>
            </Stack>
          </>
        )}
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', pt: 2 }}>
          <Button
            disabled={formik.isSubmitting}
            onClick={() => {
              signPadRef.current?.clear()
              formik.resetForm()
            }}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
          <Button loading={formik.isSubmitting} type="submit" variant="contained">
            Save
          </Button>
        </Stack>
      </Section>
    </form>
  )
}

function ProfilePicture() {
  const me = useMe()

  const updatePicture = usePictureUpdate()
  const removePicture = usePictureRemove()

  const handlePictureUpdate = (image) => {
    return updatePicture
      .mutateAsync({ mime_type: 'image/jpeg', base_64_image: image.split(',')[1] })
      .then(() => toast.success('Profile picture updated'))
      .catch((error) => handleError(error, { showResponse: true }))
  }

  const handlePictureRemove = () => {
    return removePicture
      .mutateAsync()
      .then(() => toast.success('Profile picture removed'))
      .catch(handleError)
  }

  return <AvatarUploader user={me} readOnly={me.role === UserRole.Provider} onChange={handlePictureUpdate} onRemove={handlePictureRemove} />
}
